// src/pages/tournament/PreviousPlayers.jsx
// Search the players used in the user's earlier tournaments and add them to this one with a click.
import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '../../components/icons';

const SEARCH_DELAY_MS = 250;

// search(query) resolves to [{ firstName, lastName, timesUsed }]; onAdd(firstName, lastName) resolves truthy on success.
// `refreshKey` changes when the players of the tournament change, so the list stops offering players already added.
function PreviousPlayers({ search, onAdd, refreshKey }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [adding, setAdding] = useState(null); // the key of the player being added
    const boxRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const found = await search(query.trim());
                if (!cancelled) setResults(found);
            } catch (error) {
                if (!cancelled) setResults([]);
            }
        }, query ? SEARCH_DELAY_MS : 0);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, open, search, refreshKey]);

    const add = async (player) => {
        const key = `${player.firstName} ${player.lastName}`;
        setAdding(key);
        const added = await onAdd(player.firstName, player.lastName);
        setAdding(null);
        if (added) setResults((current) => current.filter((candidate) => candidate !== player));
    };

    const closeWhenFocusLeaves = (event) => {
        if (!boxRef.current?.contains(event.relatedTarget)) setOpen(false);
    };

    return (
        <div className="previous-players" ref={boxRef} onBlur={closeWhenFocusLeaves}>
            <label className="sr-only" htmlFor="previous-players-search">
                Find players from earlier tournaments
            </label>
            <input
                id="previous-players-search"
                className="input"
                type="search"
                autoComplete="off"
                placeholder="Find players from earlier tournaments"
                value={query}
                onFocus={() => setOpen(true)}
                onChange={(event) => setQuery(event.target.value)}
            />
            {open && (
                <ul
                    className="previous-results"
                    aria-label="Players from earlier tournaments"
                    onMouseDown={(event) => event.preventDefault()} // Safari gives a clicked button no focus: the list would close before the click
                >
                    {results.length === 0 ? (
                        <li className="hint previous-empty">
                            {query ? 'Nobody found.' : 'Players from your earlier tournaments show up here.'}
                        </li>
                    ) : (
                        results.map((player) => {
                            const key = `${player.firstName} ${player.lastName}`;
                            return (
                                <li key={key}>
                                    <button
                                        type="button"
                                        className="previous-result"
                                        disabled={adding === key}
                                        aria-label={`Add ${key}`}
                                        onClick={() => add(player)}
                                    >
                                        <span className="previous-name">{key}</span>
                                        <span className="hint">
                                            {player.timesUsed} tournament{player.timesUsed === 1 ? '' : 's'}
                                        </span>
                                        <PlusIcon size={16} />
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            )}
        </div>
    );
}

export default PreviousPlayers;
